import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CopyIcon,
  DotsThreeCircleVerticalIcon,
  MapPinIcon,
  PencilSimpleIcon,
  StorefrontIcon,
  TrashIcon,
  WarehouseIcon,
} from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import {
  ButtonMenu,
  Chip,
  helpers,
  IconButton,
  locationTypes,
  ReveniContractChip,
  Switch,
  Text,
} from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import { shippingMethodVisibility } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/ShippingMethods/constants'

import Ranges from './Ranges/Ranges'
import { getFormatPrice } from './helpers'
import * as Styles from './styles'

const ShippingMethod = ({
  data,
  onEdit,
  onDelete,
  onDuplicate,
  onChangeEnabled,
  regions,
  index,
  isEditable = true,
  showSwitch = true,
  showRegions = true,
  showRanges = true,
  customBackgroundColor,
  containerPadding,
}) => {
  const theme = useTheme()
  const { info } = useContext(AppContext)

  const { t, i18n } = useTranslation()

  const handleClickDeliveryMethod = () => {
    if (onEdit) {
      onEdit(data)
    }
  }

  return (
    <Styles.MainContainer
      backgroundColor={index % 2 ? theme.colors.background.secondary : 'white'}
      customBackgroundColor={customBackgroundColor}
      padding={containerPadding}
      onClick={handleClickDeliveryMethod}
    >
      {data?.internal_id && (
        <Styles.IdentifierContainer>
          <Text type="bodyMBold">#{data?.internal_id}</Text>
        </Styles.IdentifierContainer>
      )}
      <Styles.InfoContainer>
        <Styles.Header>
          <Styles.NameContainer>
            {showSwitch && (
              <Switch
                checked={data?.is_enabled}
                onChange={e => onChangeEnabled(data?.id, e.target.checked)}
                disabled={!isEditable}
                onClick={e => e.stopPropagation()}
              />
            )}

            <Styles.Name isEnabled={data?.is_enabled} showSwitch={showSwitch}>
              <Text type="titleS" tag="label">
                {data?.internal_name}
              </Text>
              {data?.carrier?.logo_url && (
                <Styles.CarrierLogo src={data?.carrier?.logo_url} alt={data?.carrier?.name} />
              )}
              {data?.visibility === shippingMethodVisibility.internal && (
                <Chip label={t('Uso interno')} backgroundColor={theme.colors.warning.surface} />
              )}
            </Styles.Name>
          </Styles.NameContainer>
          {isEditable && (
            <Styles.Actions>
              <ButtonMenu
                stopPropagation
                menuItems={[
                  {
                    label: (
                      <Styles.MenuItem>
                        <PencilSimpleIcon />
                        {t('Editar')}
                      </Styles.MenuItem>
                    ),
                    onClick: () => onEdit(data),
                  },
                  {
                    label: (
                      <Styles.MenuItem>
                        <CopyIcon />
                        {t('Duplicar')}
                      </Styles.MenuItem>
                    ),
                    onClick: () => onDuplicate(data),
                  },
                  {
                    label: (
                      <Styles.MenuItem>
                        <TrashIcon />
                        {t('Eliminar')}
                      </Styles.MenuItem>
                    ),
                    onClick: () => onDelete(data?.id),
                  },
                ]}
                renderButton={onClick => (
                  <IconButton
                    onClick={onClick}
                    variant="text"
                    icon={<DotsThreeCircleVerticalIcon size={22} weight="light" data-testid="shipping-method-menu" />}
                  />
                )}
              />
            </Styles.Actions>
          )}
        </Styles.Header>
        <Styles.TagsAndRegionsContainer>
          <Styles.TagsContainer>
            <Chip
              label={<Text type="bodySBold">{getFormatPrice(data?.price, data?.currency, i18n.language)}</Text>}
              disabled={!data?.is_enabled}
              color={theme.colors.accent.hover}
              backgroundColor={theme.colors.warning.surface}
            />
            <Chip label={data?.carrier?.service_name} color={theme.colors.info.onSurface} truncate />
            {data?.carrier?.is_pickup_point && <Chip Icon={MapPinIcon} label={t('Punto de recogida')} />}
            {data?.carrier?.is_reveni_contract && <ReveniContractChip disabled={!data?.is_enabled} />}
            {data?.location && (
              <Chip
                alt="location"
                size={24}
                Icon={location?.type === locationTypes.store ? StorefrontIcon : WarehouseIcon}
                label={`${data?.location?.internal_id} | ${data?.location?.internal_name}`}
                color={data?.is_enabled ? theme.colors.warning.onSurface : theme.colors.text.subtle}
                backgroundColor={data?.is_enabled ? theme.colors.warning.surface : theme.colors.disabled.surface}
              />
            )}
          </Styles.TagsContainer>
          <Styles.ConfigInfoContainer>
            {showRegions && (
              <>
                <MapPinIcon size={16} color={theme.colors.text.subtle} />
                <Styles.RegionsText type="bodySRegular" isEnabled={data?.is_enabled}>
                  {helpers.getRegionsLabel(data?.regions, regions)}
                </Styles.RegionsText>
              </>
            )}
            {showRanges && (
              <Ranges
                displayCriteria={data?.display_criteria}
                storeCurrency={info?.selectedStore?.currency}
                language={i18n.language}
              />
            )}
          </Styles.ConfigInfoContainer>
        </Styles.TagsAndRegionsContainer>
      </Styles.InfoContainer>
    </Styles.MainContainer>
  )
}

export default ShippingMethod
